/**
 * Provides a programmatic interface for tRPC mutation hooks to open the
 * `ConfirmChangeRequestModal` and resolve once the user has confirmed +
 * supplied a rationale. The Apollo path triggers the same modal via
 * `ApolloGraphqlProvider`'s `onError` link; this context is the V3
 * equivalent for tRPC.
 *
 * Usage from a mutation wrapper:
 *
 * ```ts
 * const { requestConfirmation } = useChangeRequestConfirm();
 * try {
 *   return await mutation.mutateAsync(input);
 * } catch (err) {
 *   if (isChangeRequestRequired(err)) {
 *     const confirmed = await requestConfirmation();
 *     if (!confirmed) throw err;
 *     return await mutation.mutateAsync({
 *       ...input,
 *       _confirmChangeRequest: true,
 *       _requesterComment: confirmed.requesterComment,
 *     });
 *   }
 *   throw err;
 * }
 * ```
 *
 * Spec reference: RSP-1528 Phase 6b. Closes the FormContext TODO at
 * `packages/web/src/components/form/form/FormContext.tsx:326`.
 */
import type { FC, ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ConfirmChangeRequestModal } from 'src/components/change-requests-preview/ConfirmChangeRequestModal';

import type {
  ChangeRequestConfirmation,
  ChangeRequestConfirmContextValue,
} from './change-request-confirm-context';
import { ChangeRequestConfirmContext } from './change-request-confirm-context';

export const ChangeRequestConfirmProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [visible, setVisible] = useState(false);
  const resolverRef = useRef<
    ((result: ChangeRequestConfirmation | null) => void) | null
  >(null);
  // Set by `withChangeRequestConfirm` after a confirmed replay raises a CR;
  // read-and-reset by FormContext to pick the right post-submit toast +
  // navigation (RSP-6099 I/K). A ref, not state — it must not trigger a
  // re-render and is consumed within the same submit tick.
  const changeRequestCreatedRef = useRef(false);

  const markChangeRequestCreated = useCallback(() => {
    changeRequestCreatedRef.current = true;
  }, []);

  const consumeChangeRequestCreated = useCallback(() => {
    const created = changeRequestCreatedRef.current;
    changeRequestCreatedRef.current = false;

    return created;
  }, []);

  const settle = useCallback(
    (result: ChangeRequestConfirmation | null) => {
      const resolver = resolverRef.current;
      resolverRef.current = null;
      setVisible(false);
      resolver?.(result);
    },
    []
  );

  // If the provider unmounts while a confirmation is pending (e.g. the user
  // navigates away with the modal open), settle the Promise as cancelled so the
  // awaiting mutation wrapper doesn't hang forever.
  useEffect(() => {
    return () => {
      const resolver = resolverRef.current;
      resolverRef.current = null;
      resolver?.(null);
    };
  }, []);

  const requestConfirmation = useCallback((): Promise<ChangeRequestConfirmation | null> => {
    // If a previous prompt is somehow still open, settle it as cancelled
    // before opening the new one. This shouldn't happen in normal flow but
    // protects against React strict-mode double-invocations and stacked
    // retries.
    if (resolverRef.current) {
      resolverRef.current(null);
      resolverRef.current = null;
    }

    return new Promise<ChangeRequestConfirmation | null>((resolve) => {
      resolverRef.current = resolve;
      setVisible(true);
    });
  }, []);

  const value = useMemo<ChangeRequestConfirmContextValue>(
    () => ({
      requestConfirmation,
      markChangeRequestCreated,
      consumeChangeRequestCreated,
    }),
    [requestConfirmation, markChangeRequestCreated, consumeChangeRequestCreated]
  );

  return (
    <ChangeRequestConfirmContext.Provider value={value}>
      {children}
      {visible && (
        <ConfirmChangeRequestModal
          onDismiss={() => settle(null)}
          onConfirm={(requesterComment) => settle({ requesterComment })}
        />
      )}
    </ChangeRequestConfirmContext.Provider>
  );
};
